import { useState, useEffect, useRef } from 'react';
import { ShieldCheck, Copy, Check } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

type Step = 'loading' | 'scan' | 'backup_codes' | 'error';

async function getToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? '';
}

// Enroll a TOTP factor: QR + manual secret, verify the first code, then show
// the one-time backup codes (when allowed). Used full-screen by the login
// gate and embedded by Account Settings → Security.
export default function MfaEnrollmentPanel({ onComplete }: { onComplete: () => void }) {
  const { mfaRequirements } = useAuth();
  const [step, setStep] = useState<Step>('loading');
  const [factorId, setFactorId] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    startEnrollment();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startEnrollment = async () => {
    setStep('loading');
    setError('');
    try {
      // An abandoned earlier attempt leaves an unverified factor that blocks
      // a fresh enroll — clear those first. Verified factors are never
      // touched here.
      const { data: existing } = await supabase.auth.mfa.listFactors();
      for (const f of existing?.totp ?? []) {
        if (f.status !== 'verified') {
          await supabase.auth.mfa.unenroll({ factorId: f.id });
        }
      }

      const { data, error: enrollError } = await supabase.auth.mfa.enroll({ factorType: 'totp' });
      if (enrollError) throw enrollError;
      setFactorId(data.id);
      setQrCode(data.totp?.qr_code ?? null);
      setSecret(data.totp?.secret ?? null);
      setStep('scan');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start enrollment.');
      setStep('error');
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!factorId) return;
    setVerifying(true);
    setError('');
    try {
      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({ factorId });
      if (challengeError) throw challengeError;
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code: code.trim(),
      });
      if (verifyError) throw verifyError;

      // Session is aal2 now. Generate the one-time backup code set while the
      // elevated token is fresh (the function requires aal2).
      if (mfaRequirements?.backup_codes_allowed !== false) {
        const token = await getToken();
        const res = await fetch('/.netlify/functions/mfa-backup-codes', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'generate' }),
        });
        const data = await res.json();
        if (res.ok && Array.isArray(data.codes)) {
          setBackupCodes(data.codes);
          setStep('backup_codes');
          return;
        }
        console.error('Backup code generation failed:', data.error);
      }
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That code was not accepted. Check your authenticator app and try again.');
    } finally {
      setVerifying(false);
    }
  };

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(backupCodes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable — codes stay visible for manual copying.
    }
  };

  if (step === 'loading') {
    return <div className="text-center py-10 text-gray-500 text-sm">Preparing enrollment…</div>;
  }

  if (step === 'error') {
    return (
      <div className="space-y-4">
        <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>
        <button onClick={startEnrollment} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700">
          Try Again
        </button>
      </div>
    );
  }

  if (step === 'backup_codes') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-green-700">
          <ShieldCheck className="w-5 h-5" />
          <p className="text-sm font-semibold">Authenticator enrolled.</p>
        </div>
        <p className="text-sm text-gray-600">
          Save these backup codes somewhere safe. Each works once, and this is the only time they are shown.
          A backup code removes your authenticator so you can enroll a new one — it never signs you in by itself.
        </p>
        <div data-testid="mfa-backup-codes" className="grid grid-cols-2 gap-2 bg-gray-50 border border-gray-200 rounded-md p-4 font-mono text-sm">
          {backupCodes.map(c => <span key={c}>{c}</span>)}
        </div>
        <div className="flex items-center gap-3">
          <button onClick={copyCodes} className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md">
            {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied' : 'Copy codes'}
          </button>
          <button
            onClick={onComplete}
            data-testid="mfa-backup-codes-done"
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
          >
            I saved my backup codes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Scan this QR code with an authenticator app (1Password, Google Authenticator, Authy, …), then enter the 6-digit code it shows.
      </p>
      {qrCode && (
        <div className="flex justify-center bg-white border border-gray-200 rounded-md p-4" data-testid="mfa-enroll-qr">
          {qrCode.startsWith('data:') ? (
            <img src={qrCode} alt="Authenticator QR code" className="w-44 h-44" />
          ) : (
            <div className="w-44 h-44" dangerouslySetInnerHTML={{ __html: qrCode }} />
          )}
        </div>
      )}
      {/* iPhone Camera hands an otpauth QR to Apple Passwords, which then asks
          which saved login to attach it to — an unexpected screen that looks
          like something went wrong. Say so before it happens. */}
      <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
        <span className="font-medium text-gray-700">On iPhone:</span> scanning with the Camera app saves the code to
        Apple Passwords rather than your authenticator app. To use a different app, scan from inside that app — or use
        the setup key below.
      </p>
      {secret && (
        <p className="text-xs text-gray-500 text-center">
          Can't scan? Enter this key manually: <span className="font-mono select-all">{secret}</span>
        </p>
      )}
      <form onSubmit={handleVerify} className="flex gap-3">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
          required
          placeholder="123456"
          data-testid="mfa-enroll-code"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm text-center tracking-widest font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <button
          type="submit"
          disabled={verifying || code.length !== 6}
          data-testid="mfa-enroll-submit"
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-400"
        >
          {verifying ? 'Verifying…' : 'Verify'}
        </button>
      </form>
      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="mfa-enroll-error">{error}</p>}
    </div>
  );
}
